import { Stack, Typography, useTheme } from '@mui/material';
import { formatToCNPJ } from 'brazilian-values';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { obscureCNPJ } from '@woovi/utils';

import type { PaymentMethodTransactionDetails_charge$key } from './__generated__/PaymentMethodTransactionDetails_charge.graphql.ts';

type PaymentMethodTransactionDetailsProps = {
  charge: PaymentMethodTransactionDetails_charge$key;
};

const PaymentMethodTransactionDetails = (
  props: PaymentMethodTransactionDetailsProps,
): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();

  const charge = useFragment<PaymentMethodTransactionDetails_charge$key>(
    graphql`
      fragment PaymentMethodTransactionDetails_charge on Charge {
        identifier
        correlationID
        company {
          nameFriendly
          taxID {
            taxID
          }
        }
        companyBankAccount {
          id
          officialName
          taxID {
            taxID
          }
        }
      }
    `,
    props.charge,
  );

  return (
    <Stack sx={{ gap: 0.5, alignItems: 'center' }}>
      <Typography variant='h6' fontWeight='bold' textAlign='center'>
        {t('Transaction Details')}
      </Typography>
      <Stack sx={{ gap: 0 }}>
        <Typography variant='body1' fontWeight='bold' textAlign='center'>
          {t('Addressee')}
        </Typography>
        <Typography
          textAlign='center'
          sx={{ color: theme.palette.text.secondary }}
        >
          {charge?.companyBankAccount?.officialName ||
            charge?.company?.nameFriendly}
        </Typography>
        <Typography
          textAlign='center'
          sx={{ overflowWrap: 'anywhere', color: theme.palette.text.secondary }}
        >
          CNPJ:{' '}
          {obscureCNPJ(
            formatToCNPJ(
              (charge?.companyBankAccount?.taxID?.taxID ||
                charge?.company?.taxID?.taxID ||
                '') as string,
            ),
          )}
        </Typography>
      </Stack>
      <Stack sx={{ gap: 0 }}>
        <Typography variant='body1' fontWeight='bold' textAlign='center'>
          {t('Identifier')}
        </Typography>
        <Typography
          textAlign='center'
          sx={{ color: theme.palette.text.secondary }}
        >
          {charge?.identifier}
        </Typography>
      </Stack>
      <Stack sx={{ gap: 0 }}>
        <Typography variant='body1' fontWeight='bold' textAlign='center'>
          {t('CorrelationID')}
        </Typography>
        <Typography
          textAlign='center'
          sx={{ color: theme.palette.text.secondary }}
        >
          {charge?.correlationID || '-'}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default PaymentMethodTransactionDetails;
